<template>
  <div>
    <div class="visible pt-6">
      <h1
        v-if="!loading"
        class="text-lg leading-tight font-bold pb-2 xl:text-2.5xl xl:pb-4"
      >
        {{ $t('components.payment.title') }}
      </h1>

      <Loading v-if="loading && !errors.length">
        <p v-if="paymentProcessing" class="max-w-sm pt-6 mx-auto text-center xl:pt-12">
          {{ $t('components.payment.loading', brandInfo) }}
        </p>
      </Loading>

      <div v-if="errors.length" ref="error" class="w-full p-4 mt-2 mb-4 bg-sw_red_error-background">
        <p class="pb-2 text-sm font-bold leading-none xl:text-lg">
          {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
        </p>
        <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
      </div>

      <div v-if="!loading">
        <div v-if="!innerUltimateFailure" class="w-full mb-8">
          <p class="pb-4 leading-tight xl:text-xl">
            {{ $t('components.payment.description') }}
          </p>

          <div class="pb-2" :class="[formReady ? 'visible' : 'hidden']">
            <div class="mb-5">
              <TextInput
                v-model.trim="fields.name.value"
                :placeholder="$t('components.payment.form.name.placeholder')"
                :label="$t('components.payment.form.name.label')"
                :error="fields.name.valid ? '' : $t('components.payment.error.name')"
                @focus="resetFieldValidity('name')"
              />
            </div>

            <div class="w-full mb-5">
              <div
                class="w-full input-component"
                :class="{
                  'is-active': fields.cardNumber.focused || fields.cardNumber.filled,
                  'is-error': !fields.cardNumber.valid,
                }"
              >
                <div class="relative input secure-field">
                  <div id="card-number" />
                </div>

                <p v-if="!fields.cardNumber.valid">
                  <span class="block px-2 py-1 mt-1 text-xs leading-none bg-sw_red-100 text-sw_text">
                    {{ $t('components.payment.error.cardNumber') }}
                  </span>
                </p>
              </div>
            </div>

            <div class="flex w-full mb-5">
              <div class="w-1/2 mr-4">
                <TextInput
                  v-model.trim="fields.expiryDate.value"
                  v-mask="'##/##'"
                  :placeholder="$t('components.payment.form.expiryDate.placeholder')"
                  :label="$t('components.payment.form.expiryDate.label')"
                  :error="fields.expiryDate.valid ? '' : $t('components.payment.error.expiryDate')"
                  @focus="resetFieldValidity('expiryDate')"
                />
              </div>
              <div class="w-1/2">
                <div
                  class="w-full input-component"
                  :class="{ 'is-active': fields.cvv.focused || fields.cvv.filled, 'is-error': !fields.cvv.valid }"
                >
                  <div class="relative input secure-field">
                    <div id="cvv-number" />
                  </div>

                  <p v-if="!fields.cvv.valid">
                    <span class="block px-2 py-1 mt-1 text-xs leading-none bg-sw_red-100 text-sw_text">
                      {{ $t('components.payment.error.cvv') }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h1 class="text-lg leading-tight font-bold pb-2 xl:text-2.5xl xl:pb-4">
          {{ $t('components.summary.finalSummary.title') }}
        </h1>

        <div class="w-full mb-8 border border-sw_gray-300">
          <div class="p-3 text-sm xl:p-4 xl:text-base">
            <div class="flex justify-between pb-2">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.totalDue') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.voucherBalance | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>

          <div class="p-3 text-sm font-bold bg-sw_gray-50 xl:p-4 xl:text-lg">
            <div class="flex justify-between">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.creditCardAmount') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end xl:pt-4">
          <Button
            :loading="loading || secureFieldsProcessing || innerUltimateFailure"
            class="w-full xl:w-auto"
            @click="payment"
          >
            {{ $t('components.summary.submitButton') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Payment from '~psp-pages/base/redemption/payment.ts'

import Button from '~brand-components/swiss/button/button.vue'
import TextInput from '~brand-components/swiss/shared/text-input.vue'
import Loading from '~brand-components/swiss/loading/loading.vue'

export default Payment.extend({
  components: {
    Button,
    TextInput,
    Loading,
  },
  computed: {
    datatransBorder(): string {
      return '#cdcdcd'
    },
    datatransBorderError(): string {
      return '#AB3B35'
    },
    datatransStyles(): Record<string, string> {
      return {
        '*': `
          width: 100$;
          font-size: 1rem;
          color: #000000;
          background-color: #fff;
          height: 3.125rem;
          border: 1px solid #cdcdcd;
          padding: 0 1rem;
          border-radius: 0;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
          outline: 0;
        `,
        '*::placeholder': 'color: rgba(102, 102, 102, 1); opacity: 1',
        '*:-ms-input-placeholder': 'color: #666666',
      }
    },
  },
})

</script>

<style lang="scss" scoped>
.input-component {
  .input {
    @apply h-12.5;
  }
}
</style>
